import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { serifVoice } from '../../theme';
import { formatDay, parseBooking } from '../../lib/plan';
import { BookingResult, formatHours, legDetail, modeLabel, tripOutline } from '../../lib/booking';
import { useElapsed } from './useElapsed';

/** Long side, in pixels. Plenty for reading a booking; keeps the upload small. */
const MAX_SIDE = 1600;

/** Downscale to JPEG and return bare base64 — the phone screenshot can be several MB as PNG. */
function shrink(file: File): Promise<{ base64: string; mediaType: string; preview: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Could not read the image'));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      URL.revokeObjectURL(url);
      resolve({ base64: dataUrl.split(',')[1], mediaType: 'image/jpeg', preview: dataUrl });
    };
    img.onerror = () => reject(new Error('That file isn’t an image Roamoir can read'));
    img.src = url;
  });
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fill the form to check it first. */
  onApply: (result: BookingResult) => void;
  /** Create the trip now. Resolves to an error message, or null once it is made. */
  onCreate: (result: BookingResult) => Promise<string | null>;
}

/**
 * Step 1 from a booking: a screenshot of an itinerary app (TripIt, an airline
 * app) or a pasted confirmation. Roamoir reads where the trip really starts
 * and finishes — the arrival at the destination, not leaving home — plus moves
 * within it and booked stays. Shown for review before it fills the form.
 */
export default function BookingDialog({ open, onClose, onApply, onCreate }: Props) {
  const [image, setImage] = useState<{ base64: string; mediaType: string; preview: string } | null>(null);
  const [text, setText] = useState('');
  const [over, setOver] = useState(false);
  const [running, setRunning] = useState(false);
  const elapsed = useElapsed(running);
  const [result, setResult] = useState<BookingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const take = useCallback(async (file: File | undefined | null) => {
    if (!file) return;
    setError(null);
    try {
      setImage(await shrink(file));
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  function reset() {
    setImage(null);
    setText('');
    setResult(null);
    setError(null);
  }

  async function read() {
    setError(null);
    setRunning(true);
    const { data, error: err } = await parseBooking({ text: text.trim(), image: image ?? undefined });
    setRunning(false);
    if (err || !data) {
      setError(err ?? 'Nothing came back. Try again.');
      return;
    }
    if (!data.legs.length && !data.stays.length) {
      setError('Couldn’t find travel dates in that. Try a clearer screenshot, or paste the confirmation text.');
      return;
    }
    setResult(data);
  }

  const canRead = (!!image || !!text.trim()) && !running;

  return (
    <Dialog
      open={open}
      onClose={running || creating ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      // Pasting a screenshot from the clipboard is the fastest path on a Mac.
      onPaste={(e: React.ClipboardEvent) => {
        if (result) return;
        const file = Array.from(e.clipboardData.files).find((f) => f.type.startsWith('image/'));
        if (file) {
          e.preventDefault();
          take(file);
        }
      }}
    >
      <DialogTitle>Fill from a booking</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!result ? (
          <>
            <Typography variant="body2" color="text.secondary">
              A screenshot of your itinerary or airline app, or the confirmation email. Roamoir reads the whole journey,
              from leaving home to getting back, including layovers and any hotels.
            </Typography>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                take(e.dataTransfer.files[0]);
              }}
              sx={{
                border: '2px dashed',
                borderColor: over ? 'primary.main' : 'divider',
                bgcolor: over ? 'rgba(26,58,42,0.05)' : 'background.default',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                minHeight: 120,
              }}
            >
              {image ? (
                <Box component="img" src={image.preview} alt="Your booking" sx={{ height: 140, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
              ) : (
                <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              )}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {image ? 'Screenshot ready' : 'Drop a screenshot here, or paste it (⌘V)'}
                </Typography>
                <Button size="small" sx={{ mt: 0.5, ml: -1 }} onClick={() => fileInput.current?.click()} disabled={running}>
                  {image ? 'Choose a different one' : 'Choose a file'}
                </Button>
                {image ? (
                  <Button size="small" sx={{ mt: 0.5 }} onClick={() => setImage(null)} disabled={running}>
                    Remove
                  </Button>
                ) : null}
                <input ref={fileInput} type="file" accept="image/*" hidden onChange={(e) => take(e.target.files?.[0])} />
              </Box>
            </Box>
            <TextField
              label="Or paste the confirmation text"
              multiline
              minRows={4}
              maxRows={10}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={running}
            />
            {running ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Reading your booking… {elapsed}s
                </Typography>
              </Box>
            ) : null}
          </>
        ) : (
          <BookingSummary r={result} />
        )}
        {error ? <Alert severity="error">{error}</Alert> : null}
      </DialogContent>
      <DialogActions sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
        {result ? (
          <>
            <Button onClick={reset} sx={{ mr: 'auto' }} disabled={creating}>
              Try another
            </Button>
            <Button
              onClick={() => {
                onApply(result);
                reset();
              }}
              disabled={creating}
            >
              Edit first
            </Button>
            <Button
              variant="contained"
              disabled={creating}
              onClick={async () => {
                setCreating(true);
                setError(null);
                const err = await onCreate(result);
                setCreating(false);
                if (err) setError(err);
                else reset();
              }}
            >
              {creating ? <CircularProgress size={20} color="inherit" /> : 'Create trip'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button variant="contained" onClick={read} disabled={!canRead}>
              Read booking
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

const ROW = { display: 'grid', gridTemplateColumns: '64px 1fr', gap: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' } as const;

function Row({ label, strong, title, sub }: { label: string; strong?: boolean; title: string; sub?: string | null }) {
  return (
    <Box sx={ROW}>
      <Typography variant="body2" sx={strong ? { fontWeight: 700, color: 'primary.main' } : { color: 'text.secondary' }}>
        {label}
      </Typography>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {sub ? (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

const at = (date: string, time: string | null) => `${formatDay(date)}${time ? ` ${time}` : ''}`;

/**
 * The journey as it will land on the board: each leg in order, with the
 * layover between two legs shown where it happens. Start and finish are the
 * first departure and the last arrival home.
 */
function BookingSummary({ r }: { r: BookingResult }) {
  const o = tripOutline(r.legs);
  return (
    <Box>
      {r.destination ? (
        <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
          {r.destination}
        </Typography>
      ) : null}
      {o.start ? (
        <Row label="Start" strong title={`${formatDay(o.start.date, { year: true })}${o.start.time ? ` · ${o.start.time}` : ''} · ${o.start.place}`} />
      ) : null}
      {r.legs.map((l, i) => {
        const layover = o.layovers.find((x) => x.place === l.to && x.date === (l.arrive_date ?? l.date) && i < r.legs.length - 1);
        return (
          <React.Fragment key={i}>
            <Row
              label={modeLabel(l.mode)}
              title={`${l.from} → ${l.to}`}
              sub={[
                `${at(l.date, l.depart_time)} → ${l.arrive_date ? at(l.arrive_date, l.arrive_time) : l.arrive_time ?? '?'}`,
                legDetail(l),
              ]
                .filter(Boolean)
                .join(' · ')}
            />
            {layover ? (
              <Row
                label="Layover"
                strong
                title={`${layover.place}${layover.hours !== null ? ` · ${formatHours(layover.hours)}` : ''}`}
                sub={[layover.from && layover.until ? `${layover.from} – ${layover.until}` : null, 'time for a meal or a lounge'].filter(Boolean).join(' · ')}
              />
            ) : null}
          </React.Fragment>
        );
      })}
      {r.stays.map((s, i) => (
        <Row key={`s${i}`} label="Stay" title={`${s.town}${s.name ? ` · ${s.name}` : ''}`} sub={`${formatDay(s.check_in)} – ${formatDay(s.check_out)}`} />
      ))}
      {o.end ? (
        <Row label="Finish" strong title={`${formatDay(o.end.date, { year: true })}${o.end.time ? ` · ${o.end.time}` : ''} · ${o.end.place}`} />
      ) : r.legs.length ? (
        <Row label="Finish" strong title="Not in this booking" sub="The journey doesn’t come back to where it began; set the finish in the form." />
      ) : null}
      {r.notes ? (
        <Typography variant="body2" sx={{ ...serifVoice, color: 'text.secondary', mt: 1.5 }}>
          {r.notes}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Create the trip now, or edit first. Everything can be changed later on the board.
      </Typography>
    </Box>
  );
}
